import { useState, type ReactNode, type RefObject } from 'react'
import { ShopLockup } from './FlowerMark'
import { Note } from './ui'
import { useBillShare } from '../hooks/useBillShare'
import { whatsAppComposeLink } from '../lib/phone'

/**
 * The letterhead every bill opens with.
 *
 * The shop's own plate colour and its own lettering, so the bill handed across
 * the counter and the sign above it are recognisably the same shop. The name is
 * the logo artwork rather than type set to resemble it: no web font has to load
 * for the bill to look right, and nothing drifts if that font is missing.
 *
 * Solid fill, not a gradient. This block is rasterised by html2canvas on its way
 * into a PDF, and a flat colour is the one thing every version of that renders
 * identically.
 *
 * The dark plate stops at the letterhead. Below it the bill stays white, because
 * these get printed and photographed, and a page flooded with dark ink costs a
 * cartridge and reads worse, not better.
 *
 * No badge saying which kind of document this is: the body says so a line later,
 * in the name at the top and the section headings under it, and a label repeating
 * that inside the shop's own mark only crowds it.
 */
export function Letterhead({ vendor }: { vendor?: boolean }) {
  return (
    <div className={`letterhead${vendor ? ' is-vendor' : ''}`}>
      <ShopLockup width={190} />
      <div className="letterhead__tag">پھولوں کی ہول سیل — کراچی</div>
    </div>
  )
}

/**
 * Closes every bill with a signature block, so a printed sheet handed across the
 * counter carries the shop sign-off rather than ending mid-table.
 */
export function BillFooter({ vendor, note }: { vendor?: boolean; note?: ReactNode }) {
  return (
    <div className={`bill__foot${vendor ? ' is-vendor' : ''}`}>
      <div className="bill__foot-note">
        {note ?? 'حساب میں کوئی فرق نظر آئے تو فوراً بتائیں۔ شکریہ۔'}
      </div>
      <div className="sign">
        {/* Both the flourish and the line under it name the same man. The first
            attempt left "Wajid" written above عزیر, which read as two different
            people signing one bill - and the shop's own name is already in gold
            across the top of the sheet. */}
        <div className="sign__mark">Uzair</div>
        <div className="sign__rule" />
        <div className="sign__label">دستخط — عزیر</div>
      </div>
    </div>
  )
}

/*
 * Marks for the three buttons that do something with the finished bill.
 *
 * Icon AND label, never icon alone. "Save a PDF", "save a picture" and "print"
 * are three save-shaped errands whose pictures all look alike at a glance, and
 * the man using this runs a flower stall rather than a computer - the Urdu words
 * are what tell the three apart, and the mark only helps him find the right one
 * faster in a row of them. The WhatsApp button has worked this way from the
 * start; these three were the odd ones out.
 *
 * Drawn inline rather than pulled from an icon library: four small shapes do not
 * justify a dependency on a page that already loads a PDF renderer.
 */
export function PdfMark({ size = 18 }: { size?: number }) {
  return (
    <svg
      className="btn__icon"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
      <path d="M14 3v5h5" />
      <path d="M12 18v-6" />
      <path d="M9.5 15.5 12 18l2.5-2.5" />
    </svg>
  )
}

export function ImageMark({ size = 18 }: { size?: number }) {
  return (
    <svg
      className="btn__icon"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <circle cx="8.5" cy="9.5" r="1.5" />
      <path d="m21 16-4.5-4.5L7 21" />
    </svg>
  )
}

export function PrintMark({ size = 18 }: { size?: number }) {
  return (
    <svg
      className="btn__icon"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M6 9V3h12v6" />
      <path d="M6 18H4a2 2 0 0 1-2-2v-4a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2h-2" />
      <rect x="6" y="14" width="12" height="7" rx="1" />
    </svg>
  )
}

export function WhatsAppMark({ size = 19 }: { size?: number }) {
  return (
    <svg
      className="btn__icon"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
    </svg>
  )
}

/**
 * The row of buttons that gets a bill out of the shop, shared by the customer
 * ledger and the grower settlement so the two can never drift apart.
 *
 * PDF, picture and share all pull in the same megabyte of rendering libraries,
 * so they share one dynamic import and one busy flag rather than every visit to
 * the app paying for code most people never touch.
 */
export function BillActions({
  billRef,
  filename,
  caption,
  waLink,
  enabled,
}: {
  billRef: RefObject<HTMLElement | null>
  filename: string
  caption: string
  /** A direct chat link when the person has a phone number saved. */
  waLink: string | null
  enabled: boolean
  /** Anything that changes a figure on the bill. */
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const share = useBillShare({
    target: billRef,
    filename,
    caption,
    enabled,
    // A change to the filename means a different bill, which is the signal that
    // the prepared picture is stale and must be thrown away.
    deps: [filename, caption],
  })

  async function withBill(
    job: (module: typeof import('../lib/pdf'), el: HTMLElement) => Promise<void>,
  ) {
    if (!billRef.current) return
    setBusy(true)
    setError(null)
    try {
      const module = await import('../lib/pdf')
      await job(module, billRef.current)
    } catch (e) {
      setError(`بل نہیں بن سکا: ${String((e as Error)?.message ?? e)}`)
    } finally {
      setBusy(false)
    }
  }

  if (!enabled) return null

  return (
    <>
      <div className="btn-row no-print">
        {/*
          The main way a bill leaves the shop. On a phone it carries the actual
          picture; on a desktop, which cannot hand a file to another app, it
          falls back to sending the figures as text.

          There is always a WhatsApp button. Where the number is saved it opens
          that chat; where it is not, it opens WhatsApp with the message ready
          and the contact is chosen there. Hiding the button for a customer with
          no number - which is what used to happen - left the shop with no way
          to send that person their bill at all.
        */}
        {share.supported ? (
          <button type="button" className="btn btn--whatsapp" onClick={share.share}>
            <WhatsAppMark />
            {share.preparing ? 'بل تیار ہو رہا ہے…' : 'واٹس ایپ پر بل بھیجیں'}
          </button>
        ) : (
          <a
            className="btn btn--whatsapp"
            href={waLink ?? whatsAppComposeLink(caption)}
            target="_blank"
            rel="noopener noreferrer"
          >
            <WhatsAppMark />
            {waLink ? 'واٹس ایپ پر بقایا بھیجیں' : 'واٹس ایپ پر بھیجیں'}
          </a>
        )}

        {/*
          The message on its own, without the bill.

          It used to be the repair for WhatsApp dropping a PDF's caption, and
          turned green after a send to tell the shop to tap it again. The bill
          goes as a picture now and the words go with it, so that job is gone -
          but the button stays, because a quick "bqaya itna hai" is its own
          errand on the days the whole bill is more than the moment needs.
        */}
        {share.supported && (
          <a
            className="btn btn--ghost"
            href={waLink ?? whatsAppComposeLink(caption)}
            target="_blank"
            rel="noopener noreferrer"
          >
            صرف پیغام بھیجیں
          </a>
        )}

      </div>

      {/* The three ways to take a copy, under the one way to send it. */}
      <div className="bill-tiles no-print">
        <button
          type="button"
          className="bill-tile bill-tile--pdf"
          disabled={busy}
          onClick={() => void withBill((m, el) => m.downloadElementAsPdf(el, filename))}
        >
          <span className="bill-tile__mark">
            <PdfMark size={24} />
          </span>
          {busy ? 'بن رہا ہے…' : 'پی ڈی ایف'}
        </button>

        <button
          type="button"
          className="bill-tile bill-tile--image"
          disabled={busy}
          onClick={() => void withBill((m, el) => m.downloadElementAsImage(el, filename))}
        >
          <span className="bill-tile__mark">
            <ImageMark size={24} />
          </span>
          تصویر
        </button>

        <button type="button" className="bill-tile" onClick={() => window.print()}>
          <span className="bill-tile__mark">
            <PrintMark size={24} />
          </span>
          پرنٹ
        </button>
      </div>

      {(share.error || error) && <Note kind="warn">{share.error ?? error}</Note>}

      {share.sent && !share.error && (
        <Note kind="info">
          بل بھیج دیا گیا — <strong>پیغام بھی ساتھ چلا گیا</strong>۔
        </Note>
      )}

      <p className="muted small no-print">
        {share.supported ? (
          <>
            «واٹس ایپ پر بل بھیجیں» دبائیں — بل کی <strong>تصویر</strong> بن کر واٹس ایپ کھل جائے
            گا، پھر نام چن کر بھیج دیں۔ <strong>حساب کا پیغام بھی ساتھ ہی چلا جاتا ہے</strong>۔
            <strong> نام واٹس ایپ خود پوچھے گا</strong>، یہ واٹس ایپ کا اپنا اصول ہے۔ لمبے بل کے لیے
            «پی ڈی ایف محفوظ کریں» بہتر ہے — تصویر واٹس ایپ تھوڑی دبا دیتا ہے۔
          </>
        ) : waLink ? (
          <>
            واٹس ایپ کا بٹن اِس نمبر پر سیدھا چیٹ کھول دے گا، حساب لکھا ہوا۔ تصویر بھیجنی ہو تو پہلے
            «تصویر محفوظ کریں» دبا کر واٹس ایپ میں لگا دیں۔
          </>
        ) : (
          <>
            اِس نام کے ساتھ نمبر نہیں لکھا، اِس لیے واٹس ایپ کھل کر <strong>خود پوچھے گا</strong> کہ
            کس کو بھیجنا ہے۔ نمبر «گاہک» والے صفحے پر ڈال دیں تو سیدھا اُسی کی چیٹ کھلے گی۔
          </>
        )}{' '}
        پرنٹ میں صرف نیچے والا بل جاتا ہے، پورا صفحہ نہیں۔
      </p>
    </>
  )
}
